import React, { useState, useEffect } from 'react';
import { useDemoData } from '../components/useDemoData';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Shield, Key, Users, CheckCircle2, ArrowRight,
  Building2, Lock, Globe, Settings, Copy, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import EmptyDataState from '../components/EmptyDataState';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEMO_PROVIDERS = [
  { id: 'okta', name: 'Okta', type: 'SAML', icon: '🔐', setupTime: '5 min', status: 'supported' },
  { id: 'azure_ad', name: 'Azure Active Directory', type: 'OIDC', icon: '🔷', setupTime: '5 min', status: 'supported' },
  { id: 'google_workspace', name: 'Google Workspace', type: 'OIDC', icon: '🟢', setupTime: '5 min', status: 'supported' },
  { id: 'onelogin', name: 'OneLogin', type: 'SAML', icon: '🔑', setupTime: '5 min', status: 'supported' },
  { id: 'ping_identity', name: 'Ping Identity', type: 'SAML', icon: '🏓', setupTime: '10 min', status: 'supported' },
  { id: 'auth0', name: 'Auth0', type: 'OIDC', icon: '🛡', setupTime: '5 min', status: 'supported' },
  { id: 'jumpcloud', name: 'JumpCloud', type: 'SAML', icon: '☁', setupTime: '10 min', status: 'supported' },
  { id: 'custom_saml', name: 'Custom SAML 2.0', type: 'SAML', icon: '⚙', setupTime: '15 min', status: 'supported' },
  { id: 'custom_oidc', name: 'Custom OIDC', type: 'OIDC', icon: '⚙', setupTime: '15 min', status: 'supported' },
];

const EnterpriseSSOPage = () => {
  const { shouldShowData } = useDemoData();
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [configuring, setConfiguring] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [ssoUrl, setSsoUrl] = useState('');
  const [clientId, setClientId] = useState('');

  if (!shouldShowData) {
    return <EmptyDataState title="Enterprise SSO" description="Configure Single Sign-On for your organization. Support for SAML 2.0 and OpenID Connect with Okta, Azure AD, Google Workspace, and more." icon={Shield} />;
  }

  const handleConfigure = () => {
    setConfiguring(true);
    setTimeout(() => {
      setConfiguring(false);
      setConfigured(true);
      toast.success('SSO configured successfully!');
    }, 2000);
  };

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto" data-testid="enterprise-sso-page">
      <div>
        <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 flex items-center gap-2">
          <Shield className="w-7 h-7 text-emerald-400" />
          Enterprise <span className="text-emerald-400">SSO</span>
        </h1>
        <p className="text-sm text-slate-400 mt-1">Configure Single Sign-On for your organization. SAML 2.0 and OpenID Connect supported.</p>
      </div>

      {!selectedProvider ? (
        <Card>
          <CardHeader><CardTitle className="flex items-center gap-2"><Key className="w-5 h-5 text-emerald-500" />Select Your Identity Provider</CardTitle></CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-3 gap-3">
              {DEMO_PROVIDERS.map(p => (
                <button key={p.id} onClick={() => setSelectedProvider(p)}
                  className="p-4 rounded-xl border-2 border-slate-200 hover:border-emerald-500 hover:bg-emerald-50 transition-all text-left group"
                  data-testid={`sso-${p.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-lg">{p.icon}</span>
                    <Badge variant="outline" className="text-[11px]">{p.type}</Badge>
                  </div>
                  <p className="font-semibold text-sm text-slate-800 group-hover:text-emerald-700">{p.name}</p>
                  <p className="text-xs text-slate-400">Setup: {p.setupTime}</p>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : !configured ? (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2"><Settings className="w-5 h-5 text-emerald-500" />Configure {selectedProvider.name}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setSelectedProvider(null)}>Change Provider</Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* CARTIEAI Service Provider Details */}
            <div className="p-4 bg-emerald-50 rounded-xl border border-cyan-100">
              <p className="text-sm font-medium text-cyan-800 mb-3">CARTIEAI Service Provider Details</p>
              <p className="text-xs text-emerald-600 mb-2">Copy these into your {selectedProvider.name} admin panel:</p>
              <div className="space-y-2">
                {[
                  { label: 'ACS URL', value: `${window.location.origin}/api/sso/callback` },
                  { label: 'Entity ID', value: `${window.location.origin}/sso/cartie-ai` },
                  { label: 'Sign-on URL', value: `${window.location.origin}/sso/login` },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between bg-white rounded-lg px-3 py-2">
                    <div><p className="text-xs text-slate-400">{item.label}</p><p className="text-xs font-mono text-slate-800">{item.value}</p></div>
                    <button onClick={() => { navigator.clipboard.writeText(item.value); toast.success('Copied!'); }} className="p-1 hover:bg-slate-100 rounded">
                      <Copy className="w-3.5 h-3.5 text-slate-400" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* IdP Configuration */}
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-3">
              <p className="text-sm font-medium text-slate-800">Your {selectedProvider.name} Details</p>
              {selectedProvider.type === 'SAML' ? (
                <>
                  <div><label className="text-xs font-medium text-slate-400 mb-1 block">SSO URL (from {selectedProvider.name})</label>
                    <Input placeholder="https://your-org.okta.com/app/..." value={ssoUrl} onChange={(e) => setSsoUrl(e.target.value)} className="text-sm" data-testid="sso-url-input" /></div>
                  <div><label className="text-xs font-medium text-slate-400 mb-1 block">X.509 Certificate</label>
                    <textarea placeholder="Paste your SAML certificate here..." className="w-full h-20 px-3 py-2 text-xs font-mono border rounded-lg resize-none" /></div>
                </>
              ) : (
                <>
                  <div><label className="text-xs font-medium text-slate-400 mb-1 block">Client ID</label>
                    <Input placeholder="Your OAuth Client ID" value={clientId} onChange={(e) => setClientId(e.target.value)} className="text-sm" data-testid="client-id-input" /></div>
                  <div><label className="text-xs font-medium text-slate-400 mb-1 block">Client Secret</label>
                    <Input type="password" placeholder="Your OAuth Client Secret" className="text-sm" /></div>
                  <div><label className="text-xs font-medium text-slate-400 mb-1 block">Discovery URL (optional)</label>
                    <Input placeholder="https://.../.well-known/openid-configuration" className="text-sm" /></div>
                </>
              )}
            </div>

            <Button className="bg-emerald-500 hover:bg-cyan-600 w-full h-12" onClick={handleConfigure} disabled={configuring} data-testid="configure-sso-btn">
              {configuring ? 'Configuring...' : 'Save & Test Connection'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-emerald-200">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-800 mb-2">SSO Configured Successfully!</h3>
            <p className="text-slate-400 mb-6">{selectedProvider.name} is now connected. Your team can sign in using their organization credentials.</p>
            <div className="p-4 bg-slate-50 rounded-xl inline-block">
              <p className="text-xs text-slate-400 mb-1">Login URL for your team:</p>
              <p className="text-sm font-mono text-emerald-600">{`${window.location.origin}/sso/login/your-org`}</p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EnterpriseSSOPage;
